import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import AppIcon from '../../../shared/components/Icon';
import { getOperatorsForTransactionType, getDataTypeIcon } from './TransactionPropertyFilterModule';
import axiosInstance from '../../../shared/services/api/axiosInstance';

/**
 * Operator dropdown - same as events (gray pill, dropdown on click).
 */
const OperatorDropdown = ({ value, operators, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const safeOperators = Array.isArray(operators) && operators.length > 0 ? operators : [{ value: 'is', label: 'Is' }];
  const selectedOperator = safeOperators.find((op) => op.value === value) || safeOperators[0];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[88px]"
      >
        {selectedOperator.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${position.width || 100}px`,
                minWidth: '100px',
              }}
            >
              <div className="py-1">
                {safeOperators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => { onChange(op.value); setIsOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${value === op.value ? 'bg-gray-200 text-[#0f172a]' : ''}`}
                  >
                    <span>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Multiselect for filter values - same UX as events (optional customStyle gray pill).
 * Supports optional lazy-loading via onLoadMore/hasMore/isLoadingMore.
 */
const PropertyMultiselectInput = ({
  value = [],
  options = [],
  onChange,
  placeholder = 'Select values...',
  customStyle = false,
  minDropdownWidth = 200,
  onLoadMore = null,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(
    (opt) =>
      opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    onChange(newValue);
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 16;

      // Match dropdown width to trigger, but never smaller than minDropdownWidth
      const dropdownWidth = Math.max(rect.width, minDropdownWidth);
      // Approximate height based on max-h-60 (≈ 15rem)
      const dropdownHeight = 240;

      // Start aligned to the left of the trigger, just below it
      let top = rect.bottom + scrollY + 4;
      let left = rect.left + scrollX;

      // Prevent overflow on the right
      if (left + dropdownWidth > viewportWidth - margin) {
        left = viewportWidth - dropdownWidth - margin;
      }

      // Prevent overflow on the left
      if (left < margin) {
        left = margin;
      }

      // If it would overflow the bottom, flip above the trigger
      if (top + dropdownHeight > viewportHeight + scrollY - margin) {
        top = rect.top + scrollY - dropdownHeight - 4;
      }

      // Ensure we keep a margin from the top
      if (top < scrollY + margin) {
        top = scrollY + margin;
      }

      setPosition({
        top,
        left,
        width: dropdownWidth,
      });
    }
  }, [isOpen, minDropdownWidth]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current || !onLoadMore || !hasMore || isLoadingMore) return;
    const el = dropdownRef.current;
    const handleScroll = () => {
      const threshold = 40;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        onLoadMore();
      }
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen, hasMore, isLoadingMore, onLoadMore]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className={
          customStyle
            ? 'px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[120px] max-w-full text-left flex items-center justify-between gap-1'
            : 'px-2 py-1 text-sm border border-gray-300 rounded bg-white min-w-[120px] max-w-full text-left flex items-center justify-between gap-1'
        }
      >
        <span className={value.length === 0 ? 'text-[#475569]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#475569] flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${Math.max(position.width || minDropdownWidth, minDropdownWidth)}px`,
                minWidth: `${minDropdownWidth}px`,
              }}
            >
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="py-1">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[#475569]">No options found</div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleOption(option.value)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                          isSelected
                            ? 'bg-[#4b57aa] text-white hover:bg-[#4b57aa]'
                            : 'text-[#0f172a] hover:bg-gray-50'
                        }`}
                      >
                        <input type="checkbox" checked={isSelected} readOnly className="rounded" />
                        <span>{option.label || option.value}</span>
                      </button>
                    );
                  })
                )}
                {hasMore && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onLoadMore && !isLoadingMore) {
                        onLoadMore();
                      }
                    }}
                    className="w-full text-center px-3 py-2 text-xs text-[#475569] hover:bg-gray-50"
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? 'Loading more...' : 'Load more'}
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Single per-contract filter chip: same structure as events (WHERE → property → operator → value).
 * Operators and value inputs depend on datatype; values load for string Is/Is not (function names, user options).
 */
const TransactionPropertyFilterChip = ({
  filter,
  isFirst,
  onEdit,
  onRemove,
  onUpdate,
  teamId,
}) => {
  const connector = filter.connector || (isFirst ? 'WHERE' : 'AND');
  const type = filter.propertyType || 'string';
  const DataTypeIcon = getDataTypeIcon(type);
  const operators = getOperatorsForTransactionType(type);
  const defaultOp = operators[0]?.value || 'is';
  const currentOperator = filter.operator || defaultOp;

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [decodedPage, setDecodedPage] = useState(1);
  const [decodedHasMore, setDecodedHasMore] = useState(false);
  const [decodedLoadingMore, setDecodedLoadingMore] = useState(false);

  const predefinedUserProps = [
    'user.first_location_city',
    'user.first_location_country',
    'user.last_location_city',
    'user.last_location_country',
    'user.first_referrer',
    'user.last_referrer',
    'user.device_type',
    'user.browser_name',
    'user.os',
  ];

  const isDecodedProperty = typeof filter.property === 'string' && filter.property.startsWith('decoded_input.');

  const getNumericTeamId = () => {
    if (!teamId) return null;
    let numericTeamId = null;
    if (typeof teamId === 'object' && teamId !== null) {
      numericTeamId = parseInt(teamId._id || teamId.id, 10);
    } else if (typeof teamId === 'string') {
      try {
        const teamDataStr = localStorage.getItem('selectedTeamData');
        numericTeamId = teamDataStr
          ? parseInt(JSON.parse(teamDataStr)._id || JSON.parse(teamDataStr).id, 10)
          : parseInt(teamId, 10);
      } catch (e) {
        numericTeamId = parseInt(teamId, 10);
      }
    } else {
      numericTeamId = parseInt(teamId, 10);
    }
    if (isNaN(numericTeamId) || !numericTeamId) return null;
    return numericTeamId;
  };

  useEffect(() => {
    const shouldFetch =
      type === 'string' &&
      (currentOperator === 'is' || currentOperator === 'is_not') &&
      filter.property &&
      teamId;

    if (!shouldFetch) {
      setPropertyOptions([]);
      setDecodedPage(1);
      setDecodedHasMore(false);
      setDecodedLoadingMore(false);
      return;
    }

    const fetchOptions = async () => {
      const numericTeamId = getNumericTeamId();
      if (!numericTeamId && !filter.contractId) {
        setPropertyOptions([]);
        setDecodedPage(1);
        setDecodedHasMore(false);
        setDecodedLoadingMore(false);
        return;
      }

      if (filter.property === 'function_name') {
        setLoadingOptions(true);
        try {
          if (filter.contractId) {
            const res = await axiosInstance.get(
              `/transactions/contract/${encodeURIComponent(filter.contractId)}/function-names`
            );
            const names = Array.isArray(res.data?.functionNames) ? res.data.functionNames : [];
            setPropertyOptions(names.map((n) => ({ value: n, label: n })));
          } else {
            const res = await axiosInstance.get(
              `/transactions/team/${encodeURIComponent(numericTeamId)}/function-names`
            );
            const names = Array.isArray(res.data?.functionNames) ? res.data.functionNames : [];
            setPropertyOptions(names.map((n) => ({ value: n, label: n })));
          }
        } catch (e) {
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
        return;
      }

      if (filter.property && filter.property.startsWith('decoded_input.')) {
        setLoadingOptions(true);
        try {
          const key = filter.property.replace('decoded_input.', '');
          const limit = 100;

          let res;
          if (filter.contractId) {
            res = await axiosInstance.get(
              `/transactions/contract/${encodeURIComponent(filter.contractId)}`,
              {
                params: { limit, page: 1 },
              }
            );
          } else {
            res = await axiosInstance.get(
              `/transactions/team/${encodeURIComponent(numericTeamId)}`,
              {
                params: { limit, page: 1 },
              }
            );
          }

          const transactions = Array.isArray(res.data?.transactions) ? res.data.transactions : [];
          const meta = res.data?.metadata || {};
          const hasMore =
            !!meta.hasMore ||
            (typeof meta.currentPage === 'number' &&
              typeof meta.totalPages === 'number' &&
              meta.currentPage < meta.totalPages);

          const uniqueValues = new Set();
          transactions.forEach((tx) => {
            const decoded = tx.decoded_input || tx.decodedInput || {};
            if (!decoded || typeof decoded !== 'object') return;

            let raw = decoded[key];
            if (raw === undefined) {
              const lowerKey = key.toLowerCase();
              const upperKey = key.toUpperCase();
              raw = decoded[lowerKey] ?? decoded[upperKey];
            }

            if (raw !== null && raw !== undefined) {
              const str = String(raw).trim();
              if (str) uniqueValues.add(str);
            }
          });

          const options = Array.from(uniqueValues)
            .sort()
            .map((val) => ({ value: val, label: val }));

          setPropertyOptions(options);
          setDecodedPage(1);
          setDecodedHasMore(hasMore);
          setDecodedLoadingMore(false);
        } catch (e) {
          console.error('Error fetching decoded_input options:', e);
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
        return;
      }

      if (filter.propertySource === 'user' && predefinedUserProps.includes(filter.property)) {
        setLoadingOptions(true);
        try {
          const propertyParam = filter.property.replace(/^user\./, '');
          const optsResponse = await axiosInstance.get('/events/user-property-options', {
            params: { team_id: numericTeamId, property: propertyParam, limit: 500 },
          });
          if (optsResponse.data.success && Array.isArray(optsResponse.data.options)) {
            const opts = optsResponse.data.options;
            setPropertyOptions(
              Array.isArray(opts) ? opts.map((o) => (typeof o === 'string' ? { value: o, label: o } : { value: o.value ?? o.label, label: o.label ?? o.value })) : []
            );
          } else {
            setPropertyOptions([]);
          }
        } catch (e) {
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
        return;
      }

      if ((filter.property === 'event_name' || filter.property === 'eventName') || (filter.propertySource === 'event' && (filter.property === 'event_name' || filter.property === 'eventName'))) {
        setLoadingOptions(true);
        try {
          const res = await axiosInstance.get('/events/summaries', {
            params: { team_id: numericTeamId },
          });
          const summaries = (res.data?.summaries || []);
          const names = [...new Set(summaries.map((s) => s.event_name).filter(Boolean))].sort();
          setPropertyOptions(names.map((n) => ({ value: n, label: n })));
        } catch (e) {
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
        return;
      }

      setPropertyOptions([]);
    };

    fetchOptions();
  }, [type, currentOperator, filter.property, filter.propertySource, filter.contractId, teamId]);

  const handleLoadMoreDecoded = async () => {
    if (!isDecodedProperty || decodedLoadingMore || !decodedHasMore) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId && !filter.contractId) return;

    setDecodedLoadingMore(true);
    try {
      const nextPage = decodedPage + 1;
      const key = filter.property.replace('decoded_input.', '');
      const limit = 100;

      let res;
      if (filter.contractId) {
        res = await axiosInstance.get(
          `/transactions/contract/${encodeURIComponent(filter.contractId)}`,
          {
            params: { limit, page: nextPage },
          }
        );
      } else {
        res = await axiosInstance.get(
          `/transactions/team/${encodeURIComponent(numericTeamId)}`,
          {
            params: { limit, page: nextPage },
          }
        );
      }

      const transactions = Array.isArray(res.data?.transactions) ? res.data.transactions : [];
      const meta = res.data?.metadata || {};
      const hasMore =
        !!meta.hasMore ||
        (typeof meta.currentPage === 'number' &&
          typeof meta.totalPages === 'number' &&
          meta.currentPage < meta.totalPages);

      setPropertyOptions((prevOptions) => {
        const existingValues = prevOptions.map((o) => o.value);
        const uniqueValues = new Set(existingValues);

        transactions.forEach((tx) => {
          const decoded = tx.decoded_input || tx.decodedInput || {};
          if (!decoded || typeof decoded !== 'object') return;

          let raw = decoded[key];
          if (raw === undefined) {
            const lowerKey = key.toLowerCase();
            const upperKey = key.toUpperCase();
            raw = decoded[lowerKey] ?? decoded[upperKey];
          }

          if (raw !== null && raw !== undefined) {
            const str = String(raw).trim();
            if (str) uniqueValues.add(str);
          }
        });

        return Array.from(uniqueValues)
          .sort()
          .map((val) => ({ value: val, label: val }));
      });

      setDecodedPage(nextPage);
      setDecodedHasMore(hasMore);
    } catch (e) {
      console.error('Error loading more decoded_input options:', e);
    } finally {
      setDecodedLoadingMore(false);
    }
  };

  const renderValueInput = () => {
    if (type === 'string') {
      if (currentOperator === 'is_set' || currentOperator === 'is_not_set') return null;
      if (currentOperator === 'is' || currentOperator === 'is_not') {
        return (
          <PropertyMultiselectInput
            value={Array.isArray(filter.value) ? filter.value : filter.value != null ? [filter.value] : []}
            options={propertyOptions}
            onChange={(newValue) => onUpdate({ value: newValue })}
            placeholder={loadingOptions ? 'Loading values...' : 'Select values...'}
            customStyle={true}
            minDropdownWidth={isDecodedProperty ? 360 : 200}
            onLoadMore={isDecodedProperty ? handleLoadMoreDecoded : null}
            hasMore={isDecodedProperty && decodedHasMore}
            isLoadingMore={decodedLoadingMore}
          />
        );
      }
      return (
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value || null })}
          placeholder="Enter value..."
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'number') {
      if (currentOperator === 'is_numeric' || currentOperator === 'is_not_numeric') return null;
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="From..."
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569]">to</span>
            <input
              type="number"
              value={filter.value2 ?? ''}
              onChange={(e) => onUpdate({ value2: e.target.value === '' ? null : parseFloat(e.target.value) })}
              placeholder="To..."
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      return (
        <input
          type="number"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
          placeholder="Value"
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-28"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type === 'boolean') {
      return null;
    }

    if (type === 'datetime' || type === 'date') {
      if (currentOperator === 'last' || currentOperator === 'not_in_the_last') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value ?? ''}
              onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
              placeholder="X"
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <select
              value={filter.value2 || 'hours'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            {currentOperator === 'not_in_the_last' && <span className="text-xs text-[#475569]">ago</span>}
          </div>
        );
      }
      if (currentOperator === 'between' || currentOperator === 'not_between') {
        return (
          <div className="flex items-center gap-1">
            <input
              type="datetime-local"
              value={filter.value ? new Date(filter.value).toISOString().slice(0, 16) : ''}
              onChange={(e) => onUpdate({ value: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569]">to</span>
            <input
              type="datetime-local"
              value={filter.value2 ? new Date(filter.value2).toISOString().slice(0, 16) : ''}
              onChange={(e) => onUpdate({ value2: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
      }
      if (['on', 'not_on', 'before', 'since', 'in_the_next', 'before_the_last'].includes(currentOperator)) {
        if (currentOperator === 'before_the_last') {
          return (
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={filter.value ?? ''}
                onChange={(e) => onUpdate({ value: e.target.value === '' ? null : e.target.value })}
                placeholder="X"
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-16"
                onClick={(e) => e.stopPropagation()}
              />
              <select
                value={filter.value2 || 'hours'}
                onChange={(e) => onUpdate({ value2: e.target.value })}
                className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
                <option value="weeks">weeks</option>
                <option value="months">months</option>
                <option value="years">years</option>
              </select>
              <span className="text-xs text-[#475569]">ago</span>
            </div>
          );
        }
        return (
          <input
            type="datetime-local"
            value={filter.value ? new Date(filter.value).toISOString().slice(0, 16) : ''}
            onChange={(e) => onUpdate({ value: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
    }

    return (
      <input
        type="text"
        value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value ?? '')}
        onChange={(e) => onUpdate({ value: e.target.value.trim() || null })}
        placeholder="Value"
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors min-w-[120px]"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
        {isFirst ? (
          <span className="text-xs font-medium text-[#475569] px-2">WHERE</span>
        ) : (
          <OperatorDropdown
            value={connector}
            operators={[{ value: 'AND', label: 'AND' }, { value: 'OR', label: 'OR' }]}
            onChange={(newConnector) => onUpdate({ connector: newConnector })}
          />
        )}
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
          className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors min-w-0"
        >
          <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
          <span
            className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
            title={filter.propertySource === 'user' ? 'User property' : filter.propertySource === 'event' ? 'Event property' : 'Transaction property'}
          >
            {filter.propertySource === 'user' ? 'U' : filter.propertySource === 'event' ? 'E' : 'T'}
          </span>
          <span className="text-sm text-[#0f172a] font-medium truncate max-w-[140px]">{filter.propertyLabel}</span>
        </div>
        <OperatorDropdown
          value={currentOperator}
          operators={operators}
          onChange={(newOperator) => {
            const updates = { operator: newOperator, value2: null };
            if (type === 'boolean') {
              updates.value = newOperator === 'is_true';
            } else {
              updates.value = null;
            }
            onUpdate(updates);
          }}
        />
        {renderValueInput()}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1.5 text-[#475569] hover:text-[#475569] transition-colors flex-shrink-0"
        title="Delete filter"
      >
        <AppIcon name="delete" size={16} />
      </button>
    </div>
  );
};

export default TransactionPropertyFilterChip;
